import {
  minAllowedPriceMinor,
  shopTotalMinor,
  type ShopConfig,
  type ShopPaymentMethod,
  type ShopProduct,
  type ShopSaleRequest,
} from '@360face/domain';

/** Basket of the tablet "Sell a product" screen (owner 15.10.2026). Money in cents only. */
export interface CartLine {
  productId: number;
  qty: number;
  /** Price typed by the therapist (discount); undefined = list price. */
  priceMinor?: number;
}

export function addToCart(cart: CartLine[], p: ShopProduct): CartLine[] {
  const line = cart.find((l) => l.productId === p.id);
  if (!line) return p.inStock > 0 ? [...cart, { productId: p.id, qty: 1 }] : cart;
  return cart.map((l) => (l.productId === p.id ? { ...l, qty: Math.min(l.qty + 1, p.inStock, 20) } : l));
}

export function setQty(cart: CartLine[], productId: number, qty: number, inStock: number): CartLine[] {
  if (qty <= 0) return cart.filter((l) => l.productId !== productId);
  return cart.map((l) => (l.productId === productId ? { ...l, qty: Math.min(qty, inStock, 20) } : l));
}

export function setPrice(cart: CartLine[], productId: number, priceMinor: number | undefined): CartLine[] {
  return cart.map((l) => (l.productId === productId ? { ...l, priceMinor } : l));
}

/** A scanned code (Bluetooth scanner = keyboard) → product by barcode. */
export function findByBarcode(products: ShopProduct[], code: string): ShopProduct | undefined {
  const c = code.trim();
  return c ? products.find((p) => p.barcodes.includes(c)) : undefined;
}

export type CartProblem = 'empty' | 'seller' | 'method' | 'reason' | 'over_limit' | 'price';

export interface CartEval {
  lines: Array<CartLine & { product: ShopProduct; unitMinor: number; listMinor: number; discounted: boolean }>;
  /** Sum of unit prices × qty, before the payment fee. */
  subtotalMinor: number;
  /** What the client pays (fee per unit when the client pays it). */
  totalMinor: number;
  feeBp: number;
  discounted: boolean;
  problems: CartProblem[];
}

export function evalCart(
  cart: CartLine[],
  products: ShopProduct[],
  cfg: Pick<ShopConfig, 'paymentMethods' | 'maxDiscountBp'>,
  choice: { staffId?: string; method?: ShopPaymentMethod; reason: string },
): CartEval {
  const lines = cart.flatMap((l) => {
    const product = products.find((p) => p.id === l.productId);
    if (!product) return [];
    const unitMinor = l.priceMinor ?? product.priceMinor;
    return [{ ...l, product, unitMinor, listMinor: product.priceMinor, discounted: unitMinor < product.priceMinor }];
  });
  const m = cfg.paymentMethods.find((x) => x.method === choice.method);
  const fee = { feeBp: m?.feeBp ?? 0, clientPaysFee: m?.clientPaysFee ?? false };
  const priced = lines.map((l) => ({ priceMinor: l.unitMinor, qty: l.qty }));
  const discounted = lines.some((l) => l.discounted);
  const problems: CartProblem[] = [];
  if (!lines.length) problems.push('empty');
  if (lines.some((l) => !Number.isSafeInteger(l.unitMinor) || l.unitMinor < 1)) problems.push('price');
  if (lines.some((l) => l.unitMinor < minAllowedPriceMinor(l.listMinor, cfg.maxDiscountBp)))
    problems.push('over_limit');
  if (discounted && choice.reason.trim().length < 2) problems.push('reason');
  if (!choice.staffId) problems.push('seller');
  if (!choice.method) problems.push('method');
  return {
    lines,
    subtotalMinor: priced.reduce((s, l) => s + l.priceMinor * l.qty, 0),
    totalMinor: shopTotalMinor(priced, fee),
    feeBp: fee.clientPaysFee ? fee.feeBp : 0,
    discounted,
    problems,
  };
}

export function toSaleRequest(
  e: CartEval,
  choice: { staffId: string; method: ShopPaymentMethod; reason: string },
  reference: string,
): ShopSaleRequest {
  return {
    staffId: choice.staffId,
    method: choice.method,
    reference,
    ...(choice.reason.trim() ? { reason: choice.reason.trim() } : {}),
    items: e.lines.map((l) => ({
      productId: l.productId,
      qty: l.qty,
      ...(l.priceMinor !== undefined && l.priceMinor !== l.listMinor ? { priceMinor: l.priceMinor } : {}),
    })),
  };
}

/**
 * Bluetooth/USB scanners type a whole code in a few milliseconds and press Enter. Keys closer
 * than `gapMs` build a code; slower typing (a person) is ignored.
 */
export function createScanBuffer(onCode: (code: string) => void, gapMs = 80, now: () => number = () => Date.now()) {
  let buf = '';
  let last = 0;
  return (key: string): boolean => {
    const t = now();
    if (t - last > gapMs) buf = '';
    last = t;
    if (key === 'Enter') {
      const code = buf;
      buf = '';
      if (code.length >= 4) {
        onCode(code);
        return true;
      }
      return false;
    }
    if (key.length === 1) buf += key;
    return false;
  };
}
